import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import type { RfqSubmission } from '../../types';
import type { PurchaseJobPlBucket, PurchaseRequisition } from '../../types/purchasing';
import ApprovalMap from '../purchasing/ApprovalMap';
import { useJobPurchaseBudget } from '../../hooks/useJobPurchaseBudget';
import { formatQuoteTotal } from '../../utils/rfqQuoteTotal';
import { currentPendingStep } from '../../utils/purchasingApprovals';
import { PURCHASE_STATUS_LABEL } from '../../utils/purchasingStatus';
import {
  OTHER_QUOTE_PAYMENT_METHODS,
  isOtherQuotePayMethodId,
  type OtherQuotePayMethodId,
} from '../../utils/otherQuotePurchase';
import {
  formatProposalBudgetUsd,
  listProposalBucketLineItems,
  rankProposalBudgetLines,
} from '../../utils/proposalBucketLineItems';
import { isPurchaseJobPlBucket, jobPlBucketLabel } from '../../utils/purchasingSpendClass';
import './OtherQuoteApprovalModal.css';

export type OtherQuoteApprovalTarget = {
  sub: RfqSubmission;
  orgId: string;
  orgName: string;
  quoteLabel: string;
  scopeLabel: string;
  amount?: number | null;
};

export type OtherQuoteApprovalModalProps = {
  open: boolean;
  target: OtherQuoteApprovalTarget | null;
  dealId?: string;
  requisition?: PurchaseRequisition | null;
  canApprovePo?: boolean;
  canReturnPo?: boolean;
  canOverride?: boolean;
  onClose: () => void;
  onConfirmLegacy: () => Promise<void>;
  onApprovePo?: (input: {
    note?: string;
    paymentMethod?: string;
    paymentMethodId?: OtherQuotePayMethodId;
  }) => Promise<void>;
  onReturnPo?: (note: string) => Promise<void>;
};

const OtherQuoteApprovalModal: React.FC<OtherQuoteApprovalModalProps> = ({
  open,
  target,
  dealId: dealIdProp,
  requisition,
  canApprovePo,
  canReturnPo,
  onClose,
  onConfirmLegacy,
  onApprovePo,
  onReturnPo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [returnMode, setReturnMode] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<OtherQuotePayMethodId | ''>('');

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setNote('');
    setReturnMode(false);
    const existing = requisition?.paymentMethodId || target?.sub.paymentMethodId;
    setPaymentMethodId(isOtherQuotePayMethodId(existing) ? existing : '');
  }, [open, target?.sub.id, requisition?.id, requisition?.paymentMethodId, target?.sub.paymentMethodId]);

  const dealId = dealIdProp || requisition?.dealId || requisition?.costObject.dealId || '';
  const bucketRaw = requisition?.plBucket || target?.sub.plBucket;
  const bucket: PurchaseJobPlBucket | undefined = isPurchaseJobPlBucket(bucketRaw) ? bucketRaw : undefined;
  const jobBudget = useJobPurchaseBudget(open ? dealId : undefined, bucket);
  const budgetLines = useMemo(() => {
    if (!open || !bucket) return [];
    return rankProposalBudgetLines(
      listProposalBucketLineItems(jobBudget.proposal, jobBudget.changeOrders, bucket),
      target?.scopeLabel,
    );
  }, [open, bucket, jobBudget.proposal, jobBudget.changeOrders, target?.scopeLabel]);

  if (!open || !target) return null;

  const linked = !!requisition || !!target.sub.purchaseRequisitionId;
  const pending = requisition ? currentPendingStep(requisition.approvalSteps) : undefined;
  const pendingCount = requisition
    ? requisition.approvalSteps.filter(step => step.status === 'pending').length
    : 0;
  const isFinal = !!pending && pendingCount === 1;
  const needsPaymentMethod = linked && isFinal && !requisition?.paymentMethod && !requisition?.paymentMethodId;
  const amountLabel =
    target.amount != null && Number.isFinite(target.amount)
      ? formatQuoteTotal(target.amount)
      : null;
  const nodeLabel = jobPlBucketLabel(bucket || requisition?.plBucket || target.sub.plBucket);
  const payLabel = requisition?.paymentMethod || target.sub.paymentMethod;
  const quoteAmount = target.amount != null && Number.isFinite(target.amount) ? target.amount : null;
  const budgetUsd = jobBudget.budgetUsd;
  const overBudget = quoteAmount != null && budgetUsd > 0 && quoteAmount - budgetUsd > 0.005;
  const showBudgetCompare = !!bucket && !!dealId;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const run = async (fn: () => Promise<void>) => {
    setSubmitting(true);
    setError(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update this quote.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLegacyConfirm = () => run(onConfirmLegacy);

  const handleApprovePo = () =>
    run(async () => {
      if (!onApprovePo) throw new Error('Purchase order is not ready to approve.');
      if (needsPaymentMethod && !isOtherQuotePayMethodId(paymentMethodId)) {
        throw new Error('Choose Company Card or Bill Payment.');
      }
      const method = OTHER_QUOTE_PAYMENT_METHODS.find(m => m.id === paymentMethodId);
      await onApprovePo({
        note: note.trim() || undefined,
        paymentMethod: needsPaymentMethod ? method?.label : undefined,
        paymentMethodId: needsPaymentMethod && isOtherQuotePayMethodId(paymentMethodId)
          ? paymentMethodId
          : undefined,
      });
    });

  const handleReturnPo = () =>
    run(async () => {
      if (!onReturnPo) throw new Error('Purchase order is not ready to return.');
      if (!note.trim()) throw new Error('Add a note so the requester knows what to change.');
      await onReturnPo(note.trim());
    });

  return (
    <div className="ops-modal-overlay oqam-overlay" role="presentation" onClick={handleClose}>
      <div
        className={`ops-modal oqam-modal${linked ? ' oqam-modal--po' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="oqam-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ops-modal-header oqam-header">
          <div className="oqam-title-block">
            <CheckCircle2 size={18} aria-hidden className="oqam-title-icon" />
            <h3 id="oqam-title">{linked ? 'Review purchase order' : 'Approve other quote'}</h3>
          </div>
          <button type="button" className="ops-modal-close" aria-label="Close" onClick={handleClose} disabled={submitting}>
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body oqam-body">
          <p className="oqam-lead">
            {linked
              ? 'This quote uses the same purchase-order approval as Purchasing. Approve or return it here or on the Purchasing page.'
              : 'You are approving this staff-uploaded quote. Other quotes are managed in CRM only — no supplier portal email is sent.'}
          </p>

          <section className="oqam-summary" aria-label="Quote summary">
            <div className="oqam-summary-primary">
              <span className="oqam-vendor">{target.orgName}</span>
              {amountLabel && (
                <div className={`oqam-amount-block${overBudget ? ' oqam-amount-block--over' : ''}`}>
                  <span className="oqam-amount">{amountLabel}</span>
                  {showBudgetCompare && (
                    <span className="oqam-budget-of">
                      {!jobBudget.proposalReady
                        ? 'Loading proposal budget…'
                        : nodeLabel
                          ? `of ${formatProposalBudgetUsd(budgetUsd)} ${nodeLabel} budgeted`
                          : `of ${formatProposalBudgetUsd(budgetUsd)} budgeted`}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="oqam-summary-meta">
              <span className="oqam-scope-badge">{target.scopeLabel}</span>
              {nodeLabel && <span className="oqam-scope-badge">{nodeLabel}</span>}
              {payLabel && <span className="oqam-quote-label">Pay by {payLabel}</span>}
            </div>
            {overBudget && nodeLabel && (
              <p className="oqam-budget-over">This quote is over the {nodeLabel} proposal budget.</p>
            )}
            {target.sub.notes?.trim() && (
              <p className="oqam-notes">{target.sub.notes.trim()}</p>
            )}
            {showBudgetCompare && jobBudget.proposalReady && budgetLines.length > 0 && (
              <div className="oqam-budget-lines" aria-label={`Proposal ${nodeLabel || 'category'} line items`}>
                <div className="oqam-budget-lines-title">
                  Proposal {nodeLabel || 'category'}
                </div>
                <ul>
                  {budgetLines.map(line => (
                    <li
                      key={line.id}
                      className={line.matched ? 'oqam-budget-line oqam-budget-line--match' : 'oqam-budget-line'}
                    >
                      <span>
                        {line.label}
                        {line.kind === 'change_order' ? ' · change order' : ''}
                      </span>
                      <strong>{formatProposalBudgetUsd(line.amount)}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {linked && requisition && (
            <section className="oqam-po" aria-label="Purchase order">
              <div className="oqam-po-line">
                <strong>{requisition.poNumber || requisition.prNumber}</strong>
                <span>{PURCHASE_STATUS_LABEL[requisition.status]}</span>
              </div>
              {pending && (
                <p className="oqam-next-step">
                  Waiting on {pending.userName || 'the next reviewer'}
                  {pending.roleLabel ? ` (${pending.roleLabel})` : ''}.
                </p>
              )}
              <ApprovalMap
                steps={requisition.approvalSteps}
                origin={{ userName: requisition.requesterName, at: requisition.createdAt }}
                followOn={
                  requisition.status === 'cancelled'
                    ? undefined
                    : {
                        roleLabel: 'Purchase',
                        userName: requisition.purchasedByName,
                        at: requisition.purchasedAt,
                        note: requisition.status === 'purchased' ? undefined : requisition.paymentMethodNote,
                        done: requisition.status === 'purchased',
                      }
                }
                bandLabel={requisition.bandLabel}
              />
            </section>
          )}

          {linked && !requisition && (
            <p className="oqam-next-step">Loading the linked purchase order…</p>
          )}

          {!linked && (
            <p className="oqam-next-step">
              After approval, an Accounts Payable bill is created and you can set payment terms.
            </p>
          )}

          {linked && canApprovePo && needsPaymentMethod && !returnMode && (
            <label className="oqam-field">
              <span>Pay method</span>
              <select
                value={paymentMethodId}
                disabled={submitting}
                onChange={(e) => setPaymentMethodId(e.target.value as OtherQuotePayMethodId | '')}
              >
                <option value="">Select method…</option>
                {OTHER_QUOTE_PAYMENT_METHODS.map(method => (
                  <option key={method.id} value={method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {linked && (canApprovePo || canReturnPo) && (
            <label className="oqam-field">
              <span>{returnMode || !isFinal ? 'Return note' : 'Note (optional)'}</span>
              <textarea
                rows={2}
                value={note}
                disabled={submitting}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          )}

          {error && (
            <p className="oqam-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </p>
          )}
        </div>

        <footer className="ops-modal-footer oqam-footer">
          <button type="button" className="ops-hide-completed-btn" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          {!linked && (
            <button type="button" className="ops-new-btn" disabled={submitting} onClick={() => void handleLegacyConfirm()}>
              {submitting ? (
                <>
                  <Loader2 size={14} className="oqam-spin" aria-hidden /> Approving…
                </>
              ) : (
                'Approve quote'
              )}
            </button>
          )}
          {linked && canReturnPo && (
            <button
              type="button"
              className="ops-hide-completed-btn"
              disabled={submitting}
              onClick={() => {
                if (!returnMode) {
                  setReturnMode(true);
                  return;
                }
                void handleReturnPo();
              }}
            >
              {returnMode ? 'Confirm return' : 'Return'}
            </button>
          )}
          {linked && canApprovePo && !returnMode && (
            <button type="button" className="ops-new-btn" disabled={submitting} onClick={() => void handleApprovePo()}>
              {submitting ? (
                <>
                  <Loader2 size={14} className="oqam-spin" aria-hidden /> Approving…
                </>
              ) : isFinal ? (
                'Approve & issue PO'
              ) : (
                'Approve'
              )}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default OtherQuoteApprovalModal;
